import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from "react-router-dom";

import {
  SIDE_BAR_WIDTH,
  MAIN_CONTENT_MIN_WIDTH,
  MAIN_CONTENT_MIN_HEIGHT
} from "../../constants";
import { initialize } from "../../actions/initialize";
import { fetchClients } from "../../actions/client";
import { fetchFeeds } from "../../actions/feed";
import Home from "../Home";
import SideBar from "../SideBar";
import TopNav from "../TopNav";
import FeedDetail from "../FeedDetail";
import ClientDetail from "../ClientDetail";
import PageLoader from "../PageLoader";
import UnauthorizedApp from "../../UnauthorizedApp";
import Analytics from "../Analytics/index";

import { useAuth0 } from "@auth0/auth0-react";
import Cookies from "js-cookie";
import { fetchReportConfigs } from "../../actions/reportConfig";
import { fetchVrReportsOngoing } from "../../actions/vrReportOngoing";
import { fetchFeedOwnerUsers } from "../../actions/feedOwnerReport";
import { fetchFeedOwnerDetailUsers } from "../../actions/feedOwnerReportDetail";

const AppWrapper = styled.div`
  display: flex;
  color: #212530;
  font-family: "MaisonNeue", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.75px;
`;

const MainContainer = styled.div`
  height: 100vh;
  min-height: ${MAIN_CONTENT_MIN_HEIGHT};
  width: calc(100vw - ${SIDE_BAR_WIDTH});
  min-width: ${MAIN_CONTENT_MIN_WIDTH};
  background: #f9f9fd;
  overflow-y: scroll;
`;

const App = ({
  initialize,
  fetchClients,
  fetchFeeds,
  fetchReportConfigs,
  fetchVrReportsOngoing,
  fetchFeedOwnerUsers,
  fetchFeedOwnerDetailUsers,
  initialized,
  errorMessage,
  loading
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [cookiesSet, setCookiesSet] = useState(false);

  useEffect(() => {
    const req = async () => {
      const token = await getAccessTokenSilently({
        scope: "openid profile",
        audience: "https://api.platform.samba.tv"
      });
      if (token) {
        Cookies.set("accessToken", token);
        setCookiesSet(true);
        initialize(token);
        fetchClients(token);
        fetchFeeds(token);
        fetchReportConfigs(token);
        fetchVrReportsOngoing(token);
        fetchFeedOwnerUsers(token);
        fetchFeedOwnerDetailUsers(token);
      }
    };
    req();
  }, [getAccessTokenSilently]);

  if (!initialized) {
    return <div>{errorMessage}</div>;
  }

  if (!cookiesSet) {
    return <PageLoader />;
  }
  if (!isAuthenticated) {
    return <UnauthorizedApp />;
  }

  return (
    <AppWrapper>
      <Router>
        <SideBar />
        <MainContainer>
          <TopNav />
          {loading ? (
            <PageLoader />
          ) : (
            <Switch>
              <Route exact path="/" component={Home} />
              <Route exact path="/analytics" component={Analytics} />
              <Route
                exact
                path="/clients/:clientId/feeds/:feedId"
                component={FeedDetail}
              />
              <Route exact path="/clients/:clientId" component={ClientDetail} />
              <Redirect to="/" />
            </Switch>
          )}
        </MainContainer>
      </Router>
    </AppWrapper>
  );
};

App.propTypes = {
  initialized: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  initialize: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fetchClients: PropTypes.func,
  fetchFeeds: PropTypes.func,
  fetchFeedOwnerUsers: PropTypes.func,
  fetchFeedOwnerDetailUsers: PropTypes.func
};

export default connect(
  store => ({
    initialized: store.server.initialized,
    errorMessage: store.server.message,
    loading: store.entities.feeds.loading
  }),
  {
    initialize: token => initialize(token),
    fetchClients: token => fetchClients(token),
    fetchFeeds: token => fetchFeeds(token),
    fetchReportConfigs: token => fetchReportConfigs(token),
    fetchVrReportsOngoing: token => fetchVrReportsOngoing(token),
    fetchFeedOwnerUsers: token => fetchFeedOwnerUsers(token),
    fetchFeedOwnerDetailUsers: token => fetchFeedOwnerDetailUsers(token)
  }
)(App);
